import React, { useCallback, useEffect, useMemo } from 'react';
import { Modal, Form, InputNumber, Input } from 'antd';
import { connect } from 'react-redux';
import { Button as CrceButton } from '@crc-evans/ui-components';
import { X } from 'lucide-react';
import TechnicianLogCustomSelect from './TechnicianLogCustomSelect';
import './TechnicianLogSection.css';
import './DownTimeModal.css';

const DOWNTIME_TYPES = [
  { id: 1, name: 'Quality' },
  { id: 2, name: 'Equipment' },
  { id: 3, name: 'Weather' },
  { id: 4, name: 'Operations' },
  { id: 5, name: 'Contractor' },
];

const DOWNTIME_TYPE_NAMES = DOWNTIME_TYPES.map((t) => t.name);

const equipmentRowLabel = (e) => {
  if (!e) return '';
  const sn = e.serialNumber;
  return sn ? `${e.pihEquipmentType} — ${sn}` : (e.pihEquipmentType || '');
};

const DownTimeTypeSelect = React.memo(function DownTimeTypeSelect({ value, onChange, disabled }) {
  const display = useMemo(
    () => (value != null ? DOWNTIME_TYPES.find((t) => t.id === value)?.name || '' : ''),
    [value]
  );
  return (
    <TechnicianLogCustomSelect
      id="downtime-modal-downtype"
      options={DOWNTIME_TYPE_NAMES}
      value={display}
      onChange={(name) => {
        const t = DOWNTIME_TYPES.find((x) => x.name === name);
        onChange(t ? t.id : undefined);
      }}
      disabled={disabled}
      placeholder="Select a type"
      showSearch={false}
    />
  );
});

const DownTimeEquipmentSelect = React.memo(function DownTimeEquipmentSelect({
  value,
  onChange,
  disabled,
  equipments,
}) {
  const list = equipments || [];
  const options = useMemo(() => list.map(equipmentRowLabel), [list]);
  const display = useMemo(() => {
    if (value == null) return '';
    const e = list.find((x) => x.id === value);
    return e ? equipmentRowLabel(e) : '';
  }, [value, list]);

  const onPick = useCallback(
    (label) => {
      const eq = list.find((e) => equipmentRowLabel(e) === label);
      onChange(eq ? eq.id : undefined);
    },
    [list, onChange]
  );

  return (
    <TechnicianLogCustomSelect
      id="downtime-modal-equipment"
      options={options}
      value={display}
      onChange={onPick}
      disabled={disabled}
      placeholder="Select equipment"
      showSearch
    />
  );
});

const DownTimeModal = ({
  visible,
  onCancel,
  onOk,
  downtimeEquipments,
  onFetchDowntimeEquipments,
  projectId,
  loading,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (projectId) {
      onFetchDowntimeEquipments({ projectId });
    }
  }, [projectId, onFetchDowntimeEquipments]);

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        onOk(values);
        form.resetFields();
      })
      .catch(() => {});
  };

  const modalTitle = <h3 className="modern-dsr-dt-add-modal__h-title">Add downtime entry</h3>;

  return (
    <Modal
      visible={visible}
      title={modalTitle}
      onCancel={onCancel}
      closeIcon={(
        <span className="modern-dsr-dt-add-modal__close-icon" aria-hidden>
          <X size={20} strokeWidth={2} />
        </span>
      )}
      footer={(
        <div className="modern-dsr-dt-add-modal__foot" role="group" aria-label="Add downtime actions">
          <CrceButton
            label="Cancel"
            variant="tertiary"
            size="medium"
            onClick={onCancel}
            disabled={loading}
          />
          <CrceButton
            label={loading ? 'Adding…' : 'Add'}
            variant="primary"
            size="medium"
            onClick={handleOk}
            disabled={loading}
          />
        </div>
      )}
      width={560}
      destroyOnClose
      className="modern-dsr-dt-add-modal"
      wrapClassName="modern-dsr-dt-add-modal__wrap"
      maskClosable={!loading}
      closable
      maskStyle={{ background: 'rgba(0, 0, 0, 0.5)' }}
    >
      <div className="modern-dsr-tech-log-section modern-dsr-dt-add-modal__form-scope">
        <Form form={form} layout="vertical" className="modern-dsr-dt-add-modal__form">
          <Form.Item
            name="downtimeType"
            label="Type"
            rules={[{ required: true, message: 'Select a type' }]}
            className="modern-dsr-dt-add-modal__row"
          >
            <DownTimeTypeSelect />
          </Form.Item>
          <Form.Item
            name="pihEquipmentId"
            label="Equipment"
            rules={[{ required: true, message: 'Select equipment' }]}
            className="modern-dsr-dt-add-modal__row"
          >
            <DownTimeEquipmentSelect equipments={downtimeEquipments || []} />
          </Form.Item>
          <div className="modern-dsr-dt-add-modal__row modern-dsr-dt-add-modal__row--pair">
            <Form.Item
              name="coatingDowntime"
              label="Coating downtime (minutes)"
              rules={[{ required: true, message: 'Required' }]}
            >
              <InputNumber
                className="modern-dsr-dt-add-modal__inputnum"
                min={0}
              />
            </Form.Item>
            <Form.Item
              name="contractorDowntime"
              label="Contractor downtime (minutes)"
              rules={[{ required: true, message: 'Required' }]}
            >
              <InputNumber
                className="modern-dsr-dt-add-modal__inputnum"
                min={0}
              />
            </Form.Item>
          </div>
          <Form.Item name="comments" label="Comments" className="modern-dsr-dt-add-modal__row">
            <Input.TextArea className="modern-dsr-dt-add-modal__textarea" rows={3} placeholder="Optional" />
          </Form.Item>
          <Form.Item name="issues" label="Issue" className="modern-dsr-dt-add-modal__row">
            <Input.TextArea className="modern-dsr-dt-add-modal__textarea" rows={3} placeholder="Optional" />
          </Form.Item>
          <Form.Item name="actionTaken" label="Action taken" className="modern-dsr-dt-add-modal__row">
            <Input.TextArea className="modern-dsr-dt-add-modal__textarea" rows={3} placeholder="Optional" />
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

const mapStateToProps = ({ dsrReducer }) => ({
  downtimeEquipments: dsrReducer.downtimeEquipments,
  loading: dsrReducer.isAddingDowntimeLogItem,
});

const mapDispatchToProps = (dispatch) => ({
  onFetchDowntimeEquipments: (payload) =>
    dispatch({ type: '@@team/fetch-downtime-equipments', ...payload }),
});

export default connect(mapStateToProps, mapDispatchToProps)(DownTimeModal);
